// RESUME-001 — Résumé médical structuré d'une évolution de consultation, pour l'impression
// et l'aperçu (iframe srcdoc, même pattern que registre_impression dans laboratoire-delais.js).
// Contrairement à l'impression historique existante (reprise brute des composants écran dans
// l'ordre chronologique), le document est organisé dans l'ordre de lecture médicale :
// motif → anamnèse → constantes → examen clinique → hypothèses → diagnostics → examens
// complémentaires → traitement → soins → conduite à tenir, avec en-tête patient et pied
// de page signataire. Les saisies multiples d'une même section sont fusionnées (listes
// dédupliquées) ou groupées en tableau (constantes). mode : 'complet' | 'synthese'.

import { formatNumberCompact } from './number-display'

const e = valeur => String(valeur === null || valeur === undefined ? '' : valeur)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')

// Les champs riches sont des données patient : conserver la mise en forme utile,
// mais jamais les éléments actifs ni les attributs exécutables dans le srcdoc.
const balises_riches = new Set(['P', 'BR', 'DIV', 'SPAN', 'STRONG', 'B', 'EM', 'I', 'U', 'S',
    'H1', 'H2', 'H3', 'H4', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'TABLE', 'THEAD', 'TBODY',
    'TFOOT', 'TR', 'TH', 'TD', 'SUP', 'SUB', 'HR'])
const balises_interdites = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM',
    'INPUT', 'BUTTON', 'SVG', 'MATH', 'LINK', 'META', 'BASE', 'TEMPLATE', 'IMAGE', 'IMG'])
const html = valeur => {
    if (valeur === null || valeur === undefined || valeur === '') return ''
    if (typeof DOMParser === 'undefined') return e(valeur)
    const document_rich = new DOMParser().parseFromString(String(valeur), 'text/html')
    const nettoyer = parent => {
        Array.from(parent.childNodes).forEach(noeud => {
            if (noeud.nodeType === 8) {
                noeud.remove()
            } else if (noeud.nodeType === 1) {
                if (balises_interdites.has(noeud.tagName)) {
                    noeud.remove()
                    return
                }
                nettoyer(noeud)
                if (!balises_riches.has(noeud.tagName)) {
                    noeud.replaceWith(...Array.from(noeud.childNodes))
                    return
                }
                Array.from(noeud.attributes).forEach(attribut => {
                    const nom = attribut.name.toLowerCase()
                    const dimension_table = ['TD', 'TH'].includes(noeud.tagName) &&
                        ['colspan', 'rowspan'].includes(nom) && /^[1-9][0-9]?$/.test(attribut.value)
                    if (!dimension_table) noeud.removeAttribute(attribut.name)
                })
            }
        })
    }
    nettoyer(document_rich.body)
    return document_rich.body.innerHTML
}

const liste = valeur => Array.isArray(valeur) ? valeur : []

const texte = valeur => {
    if (valeur === null || valeur === undefined) return ''
    return String(valeur).trim()
}

function date_fr(value) {
    if (!value) return ''
    const date = new Date(String(value).replace(' ', 'T'))
    if (isNaN(date.getTime())) return ''
    const deux = nombre => ('0' + nombre).slice(-2)
    return deux(date.getDate()) + '/' + deux(date.getMonth() + 1) + '/' + date.getFullYear()
}

function heure_fr(value) {
    if (!value) return ''
    const date = new Date(String(value).replace(' ', 'T'))
    if (isNaN(date.getTime())) return ''
    const deux = nombre => ('0' + nombre).slice(-2)
    return deux(date.getHours()) + 'h' + deux(date.getMinutes())
}

function date_heure_fr(value) {
    const d = date_fr(value)
    if (!d) return ''
    return d + ' à ' + heure_fr(value)
}

// Union de textes en conservant l'ordre de première apparition (dédoublonnage insensible à la casse).
function union_textes(listes) {
    const vus = {}
    const resultat = []
    listes.forEach(valeur => {
        const t = texte(valeur)
        if (!t) return
        const cle = t.toLowerCase()
        if (vus[cle]) return
        vus[cle] = true
        resultat.push(t)
    })
    return resultat
}

function par_type(historiques, type) {
    return liste(historiques).filter(h => h && h.data_type === type && h.data)
}

function section(titre, corps) {
    if (!corps) return ''
    return '<section class="rubrique"><h2>' + e(titre) + '</h2>' + corps + '</section>'
}

function puce_liste(items) {
    const valeurs = union_textes(items)
    if (!valeurs.length) return ''
    return '<ul>' + valeurs.map(v => '<li>' + e(v) + '</li>').join('') + '</ul>'
}

// Champs des sections personnalisées et spécialisées (lunettes, acuité, grossesse, examen FE) :
// { type, libelle, contenu } — HTML pour « Editeur de texte », liste pour choix multiples/cases.
function rendre_champs(champs) {
    return liste(champs).map(champ => {
        if (!champ || texte(champ.libelle) === '') return ''
        let contenu = ''
        if (champ.type === 'Editeur de texte') {
            contenu = html(champ.contenu)
        } else if (Array.isArray(champ.contenu)) {
            contenu = e(champ.contenu.filter(Boolean).join(', '))
        } else {
            contenu = e(champ.contenu)
        }
        if (!contenu) return ''
        return '<p><strong>' + e(champ.libelle) + ' :</strong> ' + contenu + '</p>'
    }).filter(Boolean).join('')
}

function bloc_motif(historiques) {
    const motifs = []
    par_type(historiques, 'motif_consultation').forEach(h => {
        liste(h.data.motifs).forEach(m => motifs.push(m && m.motif))
    })
    const valeurs = union_textes(motifs)
    if (!valeurs.length) return ''
    return '<p>Le patient consulte pour : <strong>' + e(valeurs.join(', ')) + '.</strong></p>'
}

function bloc_anamnese(historiques) {
    const entrees = par_type(historiques, 'anamnese')
    if (!entrees.length) return ''
    return entrees.map(h => {
        const lignes = liste(h.data.anamneses).map(a => {
            if (!a || !texte(a.contenu)) return ''
            return '<p><strong>' + e(a.libelle) + ' :</strong> ' + e(a.contenu) + '</p>'
        }).filter(Boolean).join('')
        if (!lignes) return ''
        const quand = entrees.length > 1 ? '<p class="mention">Noté le ' + e(date_heure_fr(h.date)) + (h.user ? ' par ' + e(h.user) : '') + '</p>' : ''
        return quand + lignes
    }).filter(Boolean).join('')
}

function bloc_constantes(historiques) {
    const entrees = par_type(historiques, 'constante')
    if (!entrees.length) return ''
    const rangs = entrees.map(h => {
        const d = h.data || {}
        // Les valeurs historiques contiennent parfois déjà l'unité (« 38°5C », « 75Kg ») :
        // on ne l'ajoute que si la valeur est purement numérique.
        const avec_unite = (valeur, unite) => {
            const t = texte(valeur)
            if (!t) return ''
            return /^[\d.,\s]+$/.test(t) ? t + unite : t
        }
        const poids = parseFloat(d.poids)
        const taille_m = parseFloat(d.taille) > 3 ? parseFloat(d.taille) / 100 : parseFloat(d.taille)
        const imc = (poids > 0 && taille_m > 0) ? (poids / (taille_m * taille_m)).toFixed(1).replace('.', ',') : ''
        const complements = liste(d.constante_complementaires).map(c => {
            if (!c || !texte(c.libelle)) return ''
            const valeur = Array.isArray(c.contenu) ? c.contenu.filter(Boolean).join(', ') : c.contenu
            return texte(valeur) ? e(c.libelle) + ' : ' + e(valeur) : ''
        }).filter(Boolean).join(' · ')
        const cellules = [
            avec_unite(d.temperature, ' °C'),
            texte(d.ta_gauche) || texte(d.ta_droit) ? [d.ta_gauche, d.ta_droit].filter(v => texte(v)).join(' / ') : '',
            avec_unite(d.pouls, ' bpm'),
            avec_unite(d.poids, ' kg'),
            texte(d.taille),
            imc,
            complements
        ]
        if (!cellules.some(Boolean)) return ''
        return '<tr><td>' + e(date_heure_fr(h.date)) + '</td>' + cellules.map(c => '<td>' + (e(c) || '—') + '</td>').join('') + '</tr>'
    }).filter(Boolean).join('')
    if (!rangs) return ''
    return '<table><thead><tr><th>Relevé</th><th>T°</th><th>TA (G / D)</th><th>Pouls</th><th>Poids</th><th>Taille</th><th>IMC</th><th>Compléments</th></tr></thead><tbody>' + rangs + '</tbody></table>'
}

function bloc_examen_clinique(historiques) {
    const entrees = par_type(historiques, 'examen_clinique')
    if (!entrees.length) return ''
    return entrees.map(h => {
        const appareils = liste(h.data.examens_clinique).map(appareil => {
            if (!appareil) return ''
            const observations = liste(appareil.observations).map(o => o && o.observation ? '<div class="obs">' + html(o.observation) + '</div>' : '').filter(Boolean).join('')
            if (!observations) return ''
            return '<p><strong>' + e(appareil.type_appareil) + ' :</strong></p>' + observations
        }).filter(Boolean).join('')
        if (!appareils) return ''
        const quand = entrees.length > 1 ? '<p class="mention">Examen du ' + e(date_heure_fr(h.date)) + (h.user ? ' par ' + e(h.user) : '') + '</p>' : ''
        return quand + appareils
    }).filter(Boolean).join('')
}

function bloc_diagnostics(historiques, type) {
    const diagnostics = []
    par_type(historiques, type).forEach(h => {
        liste(h.data.diagnosis).forEach(d => diagnostics.push(d && d.diagnosis))
    })
    return puce_liste(diagnostics)
}

function bloc_analyses(historiques) {
    const entrees = par_type(historiques, 'analyse_biologique')
    if (!entrees.length) return ''
    const renseignements = union_textes(entrees.map(h => h.data.renseignement_clinique))
    const rangs = []
    entrees.forEach(h => {
        liste(h.data.analyses).forEach(demande => {
            if (!demande || !texte(demande.nom)) return
            const resultat = demande.resultat_tdr === 1 ? 'TDR positif' : (demande.resultat_tdr === 0 ? 'TDR négatif' : '')
            rangs.push('<tr><td>' + e(demande.nom) + '</td><td>' + (e(demande.commentaires) || '—') + '</td><td>' + (resultat || '—') + '</td></tr>')
        })
    })
    if (!rangs.length) return ''
    return (renseignements.length ? '<p><strong>Renseignement clinique :</strong> ' + e(renseignements.join(' · ')) + '</p>' : '') +
        '<table><thead><tr><th>Analyse</th><th>Commentaire</th><th>Résultat rapide</th></tr></thead><tbody>' + rangs.join('') + '</tbody></table>'
}

function bloc_imagerie(historiques) {
    const entrees = par_type(historiques, 'imagerie_medicale')
    if (!entrees.length) return ''
    const renseignements = union_textes(entrees.map(h => h.data.renseignement_clinique))
    const examens = []
    entrees.forEach(h => {
        liste(h.data.examens).forEach(demande => examens.push(demande && demande.type_examen_nom))
    })
    const liste_examens = puce_liste(examens)
    if (!liste_examens) return ''
    return (renseignements.length ? '<p><strong>Renseignement clinique :</strong> ' + e(renseignements.join(' · ')) + '</p>' : '') + liste_examens
}

function bloc_ordonnance(historiques) {
    const entrees = par_type(historiques, 'ordonnance')
    if (!entrees.length) return ''
    const rangs = []
    const vus = {}
    entrees.forEach(h => {
        liste(h.data.ordonnances).forEach(p => {
            if (!p || !texte(p.medicament)) return
            // Même médicament + même posologie prescrit deux fois dans l'évolution : une seule ligne.
            const cle = (texte(p.medicament) + '|' + texte(p.posologie)).toLowerCase()
            if (vus[cle]) return
            vus[cle] = true
            rangs.push('<tr><td><strong>' + e(p.medicament) + '</strong></td><td>' + (e(p.posologie) || '—') + '</td><td class="ctr">' + (e(formatNumberCompact(p.quantite)) || '—') + '</td><td class="ctr">' + (e(p.conditionnement) || '—') + '</td><td>' + (String(p.interne) === '0' ? 'Externe' : 'Interne') + '</td></tr>')
        })
    })
    if (!rangs.length) return ''
    return '<table><thead><tr><th>Médicament</th><th>Posologie</th><th>Qté</th><th>Cond.</th><th>Pharmacie</th></tr></thead><tbody>' + rangs.join('') + '</tbody></table>'
}

function bloc_protocole_soins(historiques) {
    const entrees = par_type(historiques, 'protocole_soins')
    if (!entrees.length) return ''
    return entrees.map(h => {
        const p = h.data.protocoles || {}
        let corps = ''
        const sejours = []
        if (texte(p.admission)) sejours.push('Admission : ' + e(p.admission))
        if (texte(p.chambre)) sejours.push('Chambre' + (texte(p.chambre_nb_jour) ? ' (' + e(p.chambre_nb_jour) + ' j)' : ''))
        if (texte(p.observation)) sejours.push('Observation' + (texte(p.observation_nb_jour) ? ' (' + e(p.observation_nb_jour) + ' j)' : ''))
        if (sejours.length) corps += '<p>' + sejours.join(' · ') + '</p>'
        if (liste(p.medicaments).length) {
            corps += '<table><thead><tr><th>Médicament</th><th>Qté</th><th>Administration</th><th>Source</th></tr></thead><tbody>' +
                p.medicaments.map(m => '<tr><td>' + e(m.medicament) + '</td><td class="ctr">' + e(formatNumberCompact(m.qte)) + '</td><td>' + e(m.mode_administration) + '</td><td>' + e(m.source) + '</td></tr>').join('') + '</tbody></table>'
        }
        if (liste(p.consommables).length) {
            corps += '<p><strong>Consommables :</strong> ' + p.consommables.map(c => e(c.consommable) + (texte(formatNumberCompact(c.qte)) ? ' ×' + e(formatNumberCompact(c.qte)) : '')).join(', ') + '</p>'
        }
        if (liste(p.actes).length) {
            corps += '<p><strong>Actes :</strong> ' + p.actes.map(a => e(a.acte) + (texte(formatNumberCompact(a.qte)) ? ' ×' + e(formatNumberCompact(a.qte)) : '')).join(', ') + '</p>'
        }
        liste(p.protocoles_a_suivre).forEach(prot => {
            if (!prot || !texte(prot.medicament)) return
            const horaires = liste(prot.prot_a_suivre).map(etape => e(etape)).filter(Boolean).join(' → ')
            corps += '<p><strong>' + e(prot.medicament) + '</strong>' + (horaires ? ' — ' + horaires : '') + '</p>'
        })
        liste(p.cat_consignes).forEach(cat => {
            if (!cat || !texte(cat.cat_consigne_en)) return
            corps += '<p><strong>' + e(cat.cat_consigne_en) + ' :</strong> ' + e(cat.description) + '</p>'
        })
        return corps
    }).filter(Boolean).join('')
}

function bloc_traitement(historiques) {
    const entrees = par_type(historiques, 'traitement_prot_soins')
    if (!entrees.length) return ''
    return entrees.map(h => {
        const soins = liste(h.data.traitement_soin && h.data.traitement_soin.soins_a_faire)
        return soins.map(soin => {
            if (!soin) return ''
            const rangs = liste(soin.medicaments).map(m => {
                if (!m || !texte(m.data)) return ''
                return '<tr><td>' + e(m.data) + '</td><td>' + (e(m.dosage) || '—') + '</td><td>' + (e(m.mode_administration) || '—') + '</td></tr>'
            }).filter(Boolean).join('')
            if (!rangs) return ''
            return '<p><strong>Soin en H' + e(soin.hn) + '</strong></p><table><thead><tr><th>Produit</th><th>Dosage</th><th>Administration</th></tr></thead><tbody>' + rangs + '</tbody></table>'
        }).filter(Boolean).join('')
    }).filter(Boolean).join('')
}

function bloc_infos_complementaires(historiques) {
    // Les entrées « info complémentaire » sont reconnues à leur contenu éditeur,
    // comme l'écran (v-if="historique.data.contenu"), hors types déjà structurés et hors sections custom.
    const types_structures = ['anamnese', 'motif_consultation', 'constante', 'examen_clinique', 'hypothese_diagnostique', 'diagnostic',
        'analyse_biologique', 'imagerie_medicale', 'ordonnance', 'protocole_soins', 'traitement_prot_soins']
    return liste(historiques).filter(h => h && h.data && typeof h.data.contenu === 'string' && h.data.contenu.trim() !== ''
        && types_structures.indexOf(h.data_type) === -1 && !h.data.is_custom && !h.data.champs)
        .map(h => {
            const titre = texte(h.data.titre) ? '<p><strong>' + e(h.data.titre) + '</strong></p>' : ''
            return titre + '<div class="obs">' + html(h.data.contenu) + '</div>'
        }).filter(Boolean).join('')
}

// Lecture d'une entrée, sans synthèse ni fusion entre auteurs/évolutions.
// Réutilise les rendus et le nettoyage HTML éprouvés de l'aperçu.
export function historique_lecture(note) {
    const h = note || {}
    const rendus = {
        motif_consultation: bloc_motif, anamnese: bloc_anamnese, constante: bloc_constantes,
        examen_clinique: bloc_examen_clinique,
        hypothese_diagnostique: lignes => bloc_diagnostics(lignes, 'hypothese_diagnostique'),
        diagnostic: lignes => bloc_diagnostics(lignes, 'diagnostic'),
        analyse_biologique: bloc_analyses, imagerie_medicale: bloc_imagerie,
        ordonnance: bloc_ordonnance, protocole_soins: bloc_protocole_soins,
        traitement_prot_soins: bloc_traitement
    }
    // Les champs non couverts par un rendu historique restent consultables ;
    // ne jamais masquer une note simplement parce que sa section est nouvelle.
    const detail = valeur => {
        if (valeur === null || valeur === undefined || valeur === '') return ''
        if (Array.isArray(valeur)) return '<ul>' + valeur.map(v => detail(v)).filter(Boolean).map(v => '<li>' + v + '</li>').join('') + '</ul>'
        if (typeof valeur !== 'object') return html(String(valeur))
        return Object.keys(valeur).filter(cle => !/(^id$|_id$|^is_custom$|^parent_id$)/.test(cle)).map(cle => {
            const contenu = detail(valeur[cle])
            return contenu ? '<div><strong>' + e(cle.replace(/_/g, ' ')) + ' :</strong> ' + contenu + '</div>' : ''
        }).join('')
    }
    let corps = rendus[h.data_type] ? rendus[h.data_type]([h]) :
        (h.data && (h.data.champs || h.data.is_custom) ? rendre_champs(h.data.champs) : bloc_infos_complementaires([h]))
    const complet = detail(h.data)
    if (corps && rendus[h.data_type] && complet) corps += '<details><summary>Détails enregistrés</summary>' + complet + '</details>'
    return section(h.section_name || String(h.data_type || 'Note clinique').replace(/_/g, ' '), corps || complet || '<p>Aucun contenu renseigné.</p>')
}

function bloc_sections_specialisees(historiques) {
    const types_connus = ['anamnese', 'motif_consultation', 'constante', 'examen_clinique', 'hypothese_diagnostique', 'diagnostic',
        'analyse_biologique', 'imagerie_medicale', 'ordonnance', 'protocole_soins', 'traitement_prot_soins']
    return liste(historiques).filter(h => h && h.data && (h.data.is_custom || h.data.champs) && types_connus.indexOf(h.data_type) === -1)
        .map(h => section(h.section_name || 'Section complémentaire', rendre_champs(h.data.champs)))
        .filter(Boolean).join('')
}

// ————————————————————————————————————————————————————————————————
// Rédaction médicale (mode « synthese ») — RESUME-001 v2.
// La synthèse n'est pas un filtrage des sections : c'est un texte rédigé en
// phrases, comme une lettre de consultation. Compositeur déterministe, hors
// ligne (les clients tournent sur serveurs locaux sans internet) ; la structure
// pourra être remplacée par une génération IA si le chantier crédits IA aboutit.
// ————————————————————————————————————————————————————————————————

// « A, B et C »
function phrase_liste(items) {
    const valeurs = union_textes(items)
    if (!valeurs.length) return ''
    if (valeurs.length === 1) return valeurs[0]
    return valeurs.slice(0, -1).join(', ') + ' et ' + valeurs[valeurs.length - 1]
}

// HTML d'éditeur → texte simple pour la rédaction
function texte_simple(valeur) {
    return texte(String(valeur === null || valeur === undefined ? '' : valeur)
        .replace(/<\s*br\s*\/?>/gi, '. ')
        .replace(/<\/(p|div|li|h[1-6])\s*>/gi, '. ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\s+/g, ' ')
        .replace(/\.(\s*\.)+/g, '.'))
}

// Phrase terminée proprement (point final si absent)
function finir(phrase) {
    const t = texte(phrase)
    if (!t) return ''
    return /[.!?…]$/.test(t) ? t : t + '.'
}

// Conventions typographiques françaises pour la rédaction : décimale à virgule,
// espace avant les unités accolées (« 140/52mmHg » → « 140/52 mmHg »).
function typo_fr(valeur) {
    return texte(valeur)
        .replace(/(\d)\.(\d)/g, '$1,$2')
        .replace(/(\d)\s*(°C|mmHg|bpm|mm|cm|kg|g|mg|ml|cl|l|%)\b/gi, function (m, n, u) { return n + ' ' + u })
}

// Normalise une mesure saisie (« 38°5C » → « 38,5 °C », « 75Kg » → « 75 kg »)
function mesure(valeur, unite) {
    const t = texte(valeur)
    if (!t) return ''
    const nombre = parseFloat(t.replace(',', '.').replace('°', '.'))
    if (isNaN(nombre)) return t
    return String(nombre).replace('.', ',') + ' ' + unite
}

// Taille : « 178cm » → « 178 cm », « 1m78 » / « 1,78 » → « 1,78 m »
function taille_txt(valeur) {
    const t = texte(valeur)
    if (!t) return ''
    // « cm » contient « m » : tester le centimètre en premier.
    if (/cm/i.test(t)) {
        const centimetres = parseFloat(t.replace(',', '.'))
        return isNaN(centimetres) ? t : String(centimetres).replace('.', ',') + ' cm'
    }
    if (/m/i.test(t)) {
        const metres = parseFloat(t.replace(',', '.').replace(/m/i, '.'))
        return isNaN(metres) ? t : String(metres).replace('.', ',') + ' m'
    }
    const nombre = parseFloat(t.replace(',', '.'))
    if (isNaN(nombre)) return t
    return nombre <= 3 ? String(nombre).replace('.', ',') + ' m' : String(nombre).replace('.', ',') + ' cm'
}

function motifs_de(lignes) {
    const motifs = []
    par_type(lignes, 'motif_consultation').forEach(h => {
        liste(h.data.motifs).forEach(m => motifs.push(m && m.motif))
    })
    return union_textes(motifs)
}

function diagnostics_de(lignes, type) {
    const diagnostics = []
    par_type(lignes, type).forEach(h => {
        liste(h.data.diagnosis).forEach(d => diagnostics.push(d && d.diagnosis))
    })
    return union_textes(diagnostics)
}

// Rédaction « comme un médecin » (04/10/2026), sans IA : civilité, date en toutes lettres, pas de référence de bon,
// constantes regroupées sans doublon (IMC, PAM), sigles conservés, milliers séparés, phrases et paragraphes propres.
const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

function date_objet(value) {
    if (!value) return null
    const date = new Date(String(value).replace(' ', 'T'))
    return isNaN(date.getTime()) ? null : date
}

// « dimanche 4 octobre 2026 » (« 1er » le premier du mois)
function date_longue(value) {
    const d = date_objet(value)
    if (!d) return ''
    return JOURS[d.getDay()] + ' ' + (d.getDate() === 1 ? '1er' : d.getDate()) + ' ' + MOIS[d.getMonth()] + ' ' + d.getFullYear()
}

function meme_jour(a, b) {
    const x = date_objet(a), y = date_objet(b)
    return !!(x && y && x.toDateString() === y.toDateString())
}

// Première lettre en minuscule, sauf sigle en tête (« PAM », « IMC », « HTA », « NFS »)
function minuscule_initiale(valeur) {
    const t = texte(valeur)
    if (!t) return ''
    // Saisie tout en capitales (« CÉPHALÉES », « DOULEURS PELVIENNES ») : remise en minuscules ; sigles courts gardés (HTA, AVC).
    if (t === t.toUpperCase() && /[A-ZÀ-Ý]{2}/.test(t)) {
        return t.split(/(\s+)/).map(mot => /^[A-ZÀ-Ý0-9]{2,4}$/.test(mot) ? mot : mot.toLowerCase()).join('')
    }
    const premier = t.split(/[\s'’,-]/)[0]
    if (premier.length > 1 && premier === premier.toUpperCase() && /[A-ZÀ-Ý]/.test(premier)) return t
    return t.charAt(0).toLowerCase() + t.slice(1)
}

function majuscule_initiale(valeur) {
    const t = texte(valeur)
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : ''
}

// Sans ponctuation finale, pour les éléments d'une énumération
function sans_point(valeur) {
    return texte(valeur).replace(/[\s.;,]+$/, '')
}

// Milliers séparés dans les valeurs mesurées (« 1000 mL » → « 1 000 mL »)
function milliers(valeur) {
    return texte(valeur).replace(/\b\d{4,}\b(?![,.]\d)/g, n => n.replace(/\B(?=(\d{3})+(?!\d))/g, ' '))
}

function age_txt(valeur) {
    const t = texte(valeur)
    if (!t) return ''
    if (/^\d+([.,]\d+)?$/.test(t)) return t + ' ans'
    return t.replace(/(\d)\s*ans?\b/i, '$1 ans')
}

export function synthese_redigee(lignes, ctx) {
    const patient = ctx.patient || {}
    const consultation = ctx.consultation || {}
    const paragraphes = []

    // 1. Présentation : « M. AMOUZOU Ezechiel, 18 ans, a consulté le dimanche 4 octobre 2026 à 17h32 pour céphalées
    //    et douleurs pelviennes. » La référence du bon n'apparaît pas dans la synthèse.
    const sexe_brut = patient.sexe && typeof patient.sexe === 'object' ? (patient.sexe.specificatif || '') : texte(patient.sexe)
    const sexe_code = texte(sexe_brut).charAt(0).toUpperCase()
    const civilite = sexe_code === 'M' || sexe_code === 'H' ? 'M.' : (sexe_code === 'F' ? 'Mme' : '')
    const nom = [texte(patient.nom).toUpperCase(), texte(patient.prenoms)].filter(Boolean).join(' ')
    const age = age_txt(patient.age)
    const dates = lignes.map(h => h && h.date).filter(Boolean).sort()
    const debut = dates[0]
    const motifs = motifs_de(lignes)
    let intro = nom ? (civilite ? civilite + ' ' : '') + nom : 'Le patient'
    if (age) intro += ', ' + age + ','
    intro += ' a consulté'
    if (date_objet(debut)) intro += ' le ' + date_longue(debut) + ' à ' + heure_fr(debut)
    if (texte(consultation.type_consultation)) intro += ' en ' + texte(consultation.type_consultation)
    if (motifs.length) intro += ' pour ' + phrase_liste(motifs.map(m => minuscule_initiale(sans_point(m))))
    paragraphes.push(e(finir(intro)))

    // 2. Interrogatoire : chaque rubrique garde son propre paragraphe.
    const anamneses = []
    par_type(lignes, 'anamnese').forEach(h => {
        liste(h.data.anamneses).forEach(a => {
            if (!a || !texte(a.contenu)) return
            const contenu = sans_point(typo_fr(texte_simple(a.contenu)))
            if (!contenu) return
            anamneses.push(finir(texte(a.libelle) ? majuscule_initiale(sans_point(a.libelle)) + ' : ' + minuscule_initiale(contenu) : majuscule_initiale(contenu)))
        })
    })
    anamneses.forEach((phrase, index) => {
        paragraphes.push(e((index === 0 ? 'À l’interrogatoire — ' : '') + phrase))
    })

    // 3. Constantes : un paragraphe par relevé, PAM à côté de la pression artérielle, IMC une seule fois
    par_type(lignes, 'constante').forEach(h => {
        const d = h.data || {}
        const complementaires = liste(d.constante_complementaires).map(c => {
            if (!c || !texte(c.libelle)) return null
            const valeur = Array.isArray(c.contenu) ? c.contenu.filter(Boolean).join(', ') : c.contenu
            return texte(valeur) ? { libelle: texte(c.libelle), valeur: milliers(typo_fr(texte_simple(valeur))) } : null
        }).filter(Boolean)
        const pam = cote => {
            const ligne = complementaires.find(c => /\bPAM\b|pression art[ée]rielle moyenne/i.test(c.libelle) && new RegExp(cote, 'i').test(c.libelle))
            if (ligne) ligne.utilise = true
            return ligne ? ' (PAM ' + sans_point(ligne.valeur) + ')' : ''
        }
        const elements = []
        if (texte(d.temperature)) elements.push('température ' + mesure(d.temperature, '°C'))
        // Pression artérielle : unité ajoutée si la saisie n'en porte pas (« 120/52 » → « 120/52 mmHg »).
        const tension = valeur => { const t = sans_point(typo_fr(valeur)); return /mm\s*hg|cm\s*hg/i.test(t) ? t : t + ' mmHg' }
        const gauche = texte(d.ta_gauche) ? tension(d.ta_gauche) + ' au bras gauche' + pam('gauche') : ''
        const droit = texte(d.ta_droit) ? tension(d.ta_droit) + ' au bras droit' + pam('droit') : ''
        if (gauche || droit) elements.push('pression artérielle ' + [gauche, droit].filter(Boolean).join(' et '))
        if (texte(d.pouls)) elements.push('pouls ' + mesure(d.pouls, 'bpm'))

        const poids = parseFloat(String(d.poids).replace(',', '.'))
        const taille = taille_txt(d.taille)
        const taille_m = parseFloat(String(d.taille).replace(',', '.').replace(/m/i, '.'))
        const taille_metres = taille_m > 3 ? taille_m / 100 : taille_m
        const imc = poids > 0 && taille_metres > 0 ? (poids / (taille_metres * taille_metres)).toFixed(1).replace('.', ',') : ''
        complementaires.forEach(c => {
            if (c.utilise) return
            if (/^IMC\b/i.test(c.libelle) && imc) return
            elements.push(minuscule_initiale(c.libelle) + ' ' + sans_point(c.valeur))
        })
        const morphologie = []
        if (poids > 0) morphologie.push('poids ' + mesure(d.poids, 'kg'))
        if (taille) morphologie.push('taille ' + taille)
        let phrase_morpho = morphologie.length ? majuscule_initiale(morphologie.join(', ')) : ''
        if (phrase_morpho && imc) phrase_morpho += ', soit un IMC de ' + imc + ' kg/m²'

        if (!elements.length && !phrase_morpho) return
        const quand = meme_jour(h.date, debut) ? 'à ' + heure_fr(h.date) : 'le ' + date_longue(h.date) + ' à ' + heure_fr(h.date)
        let paragraphe = elements.length ? finir('Constantes relevées ' + quand + ' : ' + elements.join(', ')) : finir('Constantes relevées ' + quand)
        if (phrase_morpho) paragraphe = elements.length ? paragraphe + ' ' + finir(phrase_morpho) : finir('Constantes relevées ' + quand + ' : ' + minuscule_initiale(phrase_morpho))
        paragraphes.push(e(paragraphe))
    })

    // 4. Examen clinique : un paragraphe par appareil
    const examens = []
    par_type(lignes, 'examen_clinique').forEach(h => {
        liste(h.data.examens_clinique).forEach(appareil => {
            if (!appareil || !texte(appareil.type_appareil)) return
            const observations = liste(appareil.observations).map(o => sans_point(typo_fr(texte_simple(o && o.observation)))).filter(Boolean)
            if (observations.length) examens.push(finir(majuscule_initiale(sans_point(appareil.type_appareil)) + ' : ' + observations.map(minuscule_initiale).join(' ; ')))
        })
    })
    examens.forEach((phrase, index) => {
        paragraphes.push(e((index === 0 ? 'À l’examen clinique — ' : '') + phrase))
    })

    // 5. Conclusion : hypothèses évoquées puis diagnostic retenu
    const conclusion = []
    const hypotheses = diagnostics_de(lignes, 'hypothese_diagnostique').map(sans_point)
    if (hypotheses.length) conclusion.push(finir((hypotheses.length > 1 ? 'Hypothèses diagnostiques évoquées : ' : 'Hypothèse diagnostique évoquée : ') + phrase_liste(hypotheses.map(minuscule_initiale))))
    const diagnostics = diagnostics_de(lignes, 'diagnostic').map(sans_point)
    if (diagnostics.length) conclusion.push(finir((diagnostics.length > 1 ? 'Diagnostics retenus : ' : 'Diagnostic retenu : ') + phrase_liste(diagnostics.map(minuscule_initiale))))
    conclusion.forEach(phrase => paragraphes.push(e(phrase)))

    // 6. Examens complémentaires
    const analyses = []
    const renseignements = []
    par_type(lignes, 'analyse_biologique').forEach(h => {
        liste(h.data.analyses).forEach(demande => analyses.push(demande && demande.nom))
        renseignements.push(h.data.renseignement_clinique)
    })
    const imageries = []
    par_type(lignes, 'imagerie_medicale').forEach(h => {
        liste(h.data.examens).forEach(demande => imageries.push(demande && demande.type_examen_nom))
        renseignements.push(h.data.renseignement_clinique)
    })
    const paraclinique = []
    if (union_textes(analyses).length) paraclinique.push('Bilan biologique : ' + phrase_liste(analyses))
    if (union_textes(imageries).length) paraclinique.push('Imagerie : ' + phrase_liste(imageries))
    if (paraclinique.length) {
        paraclinique.forEach((rubrique, index) => {
            paragraphes.push(e(finir((index === 0 ? 'Examens complémentaires demandés — ' : '') + rubrique)))
        })
        const rc = union_textes(renseignements.map(r => sans_point(texte_simple(r))))
        if (rc.length) paragraphes.push(e(finir('Renseignement clinique : ' + rc.map(minuscule_initiale).join(' ; '))))
    }

    // 7. Traitement prescrit
    const prescriptions = []
    const prescrit_vus = {}
    par_type(lignes, 'ordonnance').forEach(h => {
        liste(h.data.ordonnances).forEach(p => {
            if (!p || !texte(p.medicament)) return
            const cle = (texte(p.medicament) + '|' + texte(p.posologie)).toLowerCase()
            if (prescrit_vus[cle]) return
            prescrit_vus[cle] = true
            let ligne = sans_point(p.medicament)
            if (texte(p.posologie)) ligne += ', ' + minuscule_initiale(sans_point(typo_fr(p.posologie)))
            if (String(p.interne) === '0') ligne += ' (pharmacie externe)'
            prescriptions.push(ligne)
        })
    })
    if (prescriptions.length) paragraphes.push(e(finir('Traitement prescrit : ' + prescriptions.join(' ; '))))

    // 8. Conduite à tenir (informations complémentaires rédigées)
    const conduites = []
    liste(lignes).forEach(h => {
        if (!h || !h.data || typeof h.data.contenu !== 'string' || !texte(h.data.contenu)) return
        if (h.data.is_custom || h.data.champs) return
        const type = h.data_type
        if (['anamnese', 'motif_consultation', 'constante', 'examen_clinique', 'hypothese_diagnostique', 'diagnostic',
            'analyse_biologique', 'imagerie_medicale', 'ordonnance', 'protocole_soins', 'traitement_prot_soins'].indexOf(type) !== -1) return
        const contenu = sans_point(typo_fr(texte_simple(h.data.contenu)))
        if (!contenu) return
        conduites.push(texte(h.data.titre) && !/conduite/i.test(h.data.titre) ? majuscule_initiale(sans_point(h.data.titre)) + ' : ' + minuscule_initiale(contenu) : minuscule_initiale(contenu))
    })
    if (conduites.length) paragraphes.push(e(finir('Conduite à tenir : ' + conduites.join(' ; '))))

    if (paragraphes.length <= 1 && !motifs.length) return ''
    return paragraphes.map(p => '<p class="redaction">' + p + '</p>').join('')
}

/**
 * Document HTML complet du résumé de consultation.
 * contexte : { patient, consultation, groupe, entete, mode ('complet'|'synthese') }
 */
export function resume_consultation_impression(historiques, contexte) {
    const ctx = contexte || {}
    const patient = ctx.patient || {}
    const consultation = ctx.consultation || {}
    const entete = ctx.entete || {}
    const mode = ctx.mode === 'synthese' ? 'synthese' : 'complet'
    const lignes = liste(historiques)

    const reference = texte(consultation.reference_bon) || texte(consultation.reference_bon_parent) || ''
    const medecins = union_textes(lignes.map(h => h && h.user).filter(Boolean))
    const dates = lignes.map(h => h && h.date).filter(Boolean).sort()
    const periode = dates.length ? (date_heure_fr(dates[0]) + (dates.length > 1 ? ' → ' + date_heure_fr(dates[dates.length - 1]) : '')) : ''
    const edition = date_heure_fr(new Date().toISOString().slice(0, 16).replace('T', ' '))

    const sexe = patient.sexe && typeof patient.sexe === 'object' ? (patient.sexe.specificatif || patient.sexe.libelle || '') : texte(patient.sexe)
    // L'âge peut déjà contenir le mot « ans » selon la source (« 41 ans » ou 41).
    const age = texte(patient.age) ? (/\bans?\b/i.test(String(patient.age)) ? e(patient.age) : e(patient.age) + ' ans') : ''
    const identite = [
        '<strong>' + e([patient.nom, patient.prenoms].filter(v => texte(v)).join(' ')) + '</strong>',
        age,
        sexe ? 'Sexe : ' + e(sexe) : '',
        texte(patient.code) ? 'N° dossier : ' + e(patient.code) : '',
        texte(patient.telephone_mobile) ? 'Tél. : ' + e(patient.telephone_mobile) : ''
    ].filter(Boolean).join(' · ')
    const contexte_consultation = [
        reference ? 'Référence : ' + e(reference) : '',
        texte(consultation.type_consultation) ? e(consultation.type_consultation) : '',
        texte(ctx.groupe) ? e(ctx.groupe) : '',
        periode ? e(periode) : ''
    ].filter(Boolean).join(' · ')

    let corps
    if (mode === 'synthese') {
        // Synthèse rédigée : rédaction médicale narrative couvrant toute l'évolution.
        corps = synthese_redigee(lignes, { patient, consultation, groupe: ctx.groupe })
    } else if (Array.isArray(ctx.evolutions)) {
        // Lecture intégrale : chaque épisode et chaque auteur reste identifiable.
        // Les anciens appels conservent leur rendu structuré par rubrique.
        const vus = new Set()
        const entree = h => {
            if (!h) return ''
            if (h.historique_id != null) vus.add(String(h.historique_id))
            const auteur = h.author || h.user
            const provenance = [date_heure_fr(h.date), auteur].filter(Boolean).map(e).join(' · ')
            const contenu = h.readable === false ? section(h.section_name || 'Note clinique', '<p>Le contenu de cette note ancienne n’a pas pu être lu.</p>')
                : historique_lecture(h).replace(/<details>/g, '<details open>')
            return '<article class="note-timeline">' + (provenance ? '<p class="mention">' + provenance + '</p>' : '') + contenu + '</article>'
        }
        corps = ctx.evolutions.map(groupe => '<section class="evolution-timeline"><h2>' +
            e(groupe.label || 'Évolution non libellée') + '</h2>' +
            (groupe.date ? '<p class="mention">' + e(date_heure_fr(groupe.date)) + '</p>' : '') +
            liste(groupe.entries).map(entree).join('') + '</section>').join('')
        const autres = lignes.filter(h => h && (h.historique_id == null || !vus.has(String(h.historique_id))))
        if (autres.length) corps += '<section class="evolution-timeline"><h2>Autres notes enregistrées</h2>' + autres.map(entree).join('') + '</section>'
    } else {
        const motif = section('Motif de consultation', bloc_motif(lignes))
        const anamnese = section('Anamnèse', bloc_anamnese(lignes))
        const constantes = section('Constantes', bloc_constantes(lignes))
        const examen = section('Examen clinique', bloc_examen_clinique(lignes))
        const hypotheses = section('Hypothèses diagnostiques', bloc_diagnostics(lignes, 'hypothese_diagnostique'))
        const diagnostics = section('Diagnostics retenus', bloc_diagnostics(lignes, 'diagnostic'))
        const analyses = section('Analyses biologiques', bloc_analyses(lignes))
        const imagerie = section('Imagerie médicale', bloc_imagerie(lignes))
        const ordonnance = section('Traitement prescrit', bloc_ordonnance(lignes))
        const protocole = section('Protocole de soins', bloc_protocole_soins(lignes))
        const traitement = section('Plan de traitement', bloc_traitement(lignes))
        const conduite = section('Conduite à tenir et informations complémentaires', bloc_infos_complementaires(lignes))
        const specialisees = bloc_sections_specialisees(lignes)
        corps = [motif, anamnese, constantes, examen, hypotheses, diagnostics, analyses, imagerie, ordonnance, protocole, traitement, conduite, specialisees].filter(Boolean).join('')
    }
    const vide = '<p class="mention">Aucune donnée enregistrée pour cette évolution.</p>'

    const nom_clinique = texte(entete.nom) ? '<p class="clinique">' + e(entete.nom) + '</p>' : ''
    const coordonnees = [entete.adresse, entete.ville, entete.pays].filter(v => texte(v)).map(e).join(' · ')

    const pied = '<footer>' +
        (medecins.length ? '<p><strong>' + (medecins.length > 1 ? 'Médecins intervenus' : 'Médecin') + ' :</strong> ' + e(medecins.join(', ')) + '</p>' : '') +
        '<p class="mention">Résumé édité le ' + e(edition) + ' · ' + (mode === 'synthese' ? 'Synthèse rédigée' : 'Document complet') + '</p>' +
        '<div class="signature"><span>Signature et cachet</span></div>' +
        '</footer>'

    return '<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>' + (mode === 'synthese' ? 'Synthèse de consultation' : 'Résumé de consultation') + (reference ? ' — ' + e(reference) : '') + '</title><style>' +
        '@page { size: A4 portrait; margin: 14mm; }' +
        'body { margin: 0; color: #26314b; font-family: Arial, sans-serif; font-size: 10.5pt; line-height: 1.45; }' +
        '.clinique { font-size: 12pt; font-weight: 700; margin: 0; }' +
        'h1 { font-size: 16pt; margin: 2mm 0 1mm; }' +
        'h2 { font-size: 12pt; margin: 0 0 2mm; padding-bottom: 1mm; border-bottom: 0.4mm solid #3c70fc; color: #2b4aa0; }' +
        '.rubrique { margin-top: 5mm; break-inside: avoid-page; }' +
        '.evolution-timeline { margin-top: 7mm; } .evolution-timeline h2 { break-after: avoid; } .note-timeline .rubrique { break-inside: auto; } thead { display: table-header-group; }' +
        '.identite { margin: 2mm 0; padding: 2.5mm 3mm; background: #f4f6fc; border-radius: 1.5mm; }' +
        '.contexte { color: #59677e; margin: 1mm 0 0; font-size: 9.5pt; }' +
        'table { width: 100%; border-collapse: collapse; margin: 2mm 0; }' +
        'th, td { padding: 1.6mm 2mm; border: 0.2mm solid #d4dbea; text-align: left; vertical-align: top; overflow-wrap: anywhere; }' +
        'th { background: #f4f6fc; } td.ctr, th.ctr { text-align: center; } tr { break-inside: avoid; }' +
        'ul { margin: 1mm 0 1mm 5mm; padding: 0; } li { margin: 0.6mm 0; }' +
        'p { margin: 1mm 0; } .obs p { margin: 0.6mm 0; }' +
        '.redaction { text-align: justify; margin: 3mm 0; }' +
        '.mention { color: #59677e; font-size: 9pt; }' +
        'footer { margin-top: 8mm; }' +
        '.signature { margin-top: 14mm; display: flex; justify-content: flex-end; } .signature span { border-top: 0.3mm solid #26314b; padding-top: 1.5mm; font-size: 9.5pt; color: #59677e; }' +
        '</style></head><body>' +
        nom_clinique + (coordonnees ? '<p class="mention">' + coordonnees + '</p>' : '') +
        '<h1>' + (mode === 'synthese' ? 'Synthèse de consultation' : 'Résumé de consultation') + '</h1>' +
        '<p class="identite">' + identite + '</p>' +
        (contexte_consultation ? '<p class="contexte">' + contexte_consultation + '</p>' : '') +
        (corps || vide) +
        pied +
        '</body></html>'
}
